import { useState, useEffect, useMemo, useRef } from "react";
import type { Asset, TWRChart, BenchmarkPoint } from "@/schemas";
import { getBenchmarkChart } from "../services/portfolio-service";
import { alignBenchmarkToTWR } from "../utils/benchmark-utils";
import { mergeTWRWithBenchmark, calculateTWRYAxisDomain } from "../utils/twr-utils";

interface UseBenchmarkResult {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  asset: Asset | null;
  setAsset: (asset: Asset | null) => void;
  alignedData: BenchmarkPoint[] | null;
  loading: boolean;
  // Chart data
  mergedChartData: { timestamp: string; twr: number; benchmark?: number }[];
  yAxisDomain: [number, number];
  // Last benchmark value for display
  lastBenchmarkReturn: number | null;
}

/**
 * Hook to manage benchmark selection, fetching, and alignment.
 */
export function useBenchmark(
  displayTwrChartData: TWRChart | null,
  fetchPeriod: string
): UseBenchmarkResult {
  const [enabled, setEnabled] = useState(false);
  const [asset, setAsset] = useState<Asset | null>(null);
  const [alignedData, setAlignedData] = useState<BenchmarkPoint[] | null>(null);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  // Fetch and align benchmark data when asset or TWR display data changes
  useEffect(() => {
    const fetchBenchmarkData = async () => {
      if (!enabled || !asset) {
        setAlignedData(null);
        setLoading(false);
        return;
      }

      // Wait for display TWR data to be available to align dates
      if (!displayTwrChartData || !displayTwrChartData.twr_points?.length) {
        setAlignedData(null);
        setLoading(false);
        return;
      }

      const requestId = ++requestIdRef.current;
      setLoading(true);
      
      try {
        // Fetch benchmark with same period as TWR
        const chart = await getBenchmarkChart(asset.asset_id, fetchPeriod);
        if (requestId !== requestIdRef.current) return;
        
        if (chart && chart.price_points && chart.price_points.length > 0) {
          const aligned = alignBenchmarkToTWR(chart, displayTwrChartData);
          setAlignedData(aligned);
        } else {
          setAlignedData(null);
        }
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        console.error("Error fetching benchmark data:", error);
        setAlignedData(null);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    fetchBenchmarkData();
  }, [asset, enabled, fetchPeriod, displayTwrChartData]);

  // Merge TWR and benchmark data for the chart
  const mergedChartData = useMemo(
    () => mergeTWRWithBenchmark(displayTwrChartData, alignedData, enabled),
    [displayTwrChartData, alignedData, enabled]
  );

  // Calculate Y-axis domain
  const yAxisDomain = useMemo(
    () => calculateTWRYAxisDomain(displayTwrChartData, alignedData, enabled),
    [displayTwrChartData, alignedData, enabled]
  );

  // Last benchmark return for display
  const lastBenchmarkReturn = useMemo(() => {
    if (!enabled || !alignedData || alignedData.length === 0) return null;
    return alignedData[alignedData.length - 1].return_pct;
  }, [enabled, alignedData]);

  return {
    enabled,
    setEnabled,
    asset,
    setAsset,
    alignedData,
    loading,
    mergedChartData,
    yAxisDomain,
    lastBenchmarkReturn,
  };
}
